import React, { useState, useEffect, useCallback } from 'react';
import api from '../../../services/api';
import {
  opisSrodowiska,
  skrocCommit,
  formatujDate,
  pobierzAktualnaWersje,
  pobierzHistorieWdrozen
} from '../../../utils/wersja';

// Kontrola wersji — widoczna WYLACZNIE w panelu administratora.
// Zakladka sklada sie z trzech czesci:
//   1. boks aktualnej wersji (co dokladnie dziala na tym serwerze),
//   2. historia wdrozen — wpisy powstajace automatycznie przy ./deploy.sh,
//   3. changelog redagowany recznie przez admina (istniejacy modul).

const ODSTEP_SEKCJI = '1.5rem';

// Wspolny styl "plakietki" (span, nie button — regula motywu z !important
// dotyczy tylko <button>, wiec tutaj klasy nie sa potrzebne)
const stylPlakietki = (tlo, tekst) => ({
  backgroundColor: tlo,
  color: tekst,
  padding: '0.2rem 0.5rem',
  borderRadius: '4px',
  fontSize: '0.7rem',
  fontWeight: '700',
  letterSpacing: '0.03em',
  whiteSpace: 'nowrap'
});

const stylCommita = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  fontSize: '0.75rem',
  backgroundColor: 'var(--bg-secondary)',
  color: 'var(--text-secondary)',
  padding: '0.15rem 0.4rem',
  borderRadius: '4px'
};

// Plakietka srodowiska (TEST / PRODUKCJA / LOKALNE)
const PlakietkaSrodowiska = ({ srodowisko, duza = false }) => {
  const opis = opisSrodowiska(srodowisko);
  return (
    <span
      title={opis.opis}
      style={{
        ...stylPlakietki(opis.tlo, opis.tekst),
        fontSize: duza ? '0.8rem' : '0.7rem',
        padding: duza ? '0.35rem 0.8rem' : '0.2rem 0.5rem'
      }}
    >
      {opis.etykieta}
    </span>
  );
};

// Plakietka pochodzenia wpisu — automat z deploya kontra reka admina
const PlakietkaZrodla = ({ automatyczny }) => (
  <span
    title={automatyczny
      ? 'Wpis powstał automatycznie podczas wdrożenia (deploy.sh)'
      : 'Wpis dodany ręcznie przez administratora'}
    style={stylPlakietki(
      automatyczny ? 'var(--info)' : 'var(--purple)',
      '#fff'
    )}
  >
    {automatyczny ? 'AUTOMATYCZNY' : 'RĘCZNY'}
  </span>
);

const ChangelogAdmin = () => {
  // --- changelog reczny (istniejacy modul) ---
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    version: '',
    title: '',
    description: '',
    changes: [''],
    is_important: false
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // --- kontrola wersji ---
  const [wersja, setWersja] = useState(null);
  const [ladowanieWersji, setLadowanieWersji] = useState(true);
  const [wdrozenia, setWdrozenia] = useState([]);
  const [bladWdrozen, setBladWdrozen] = useState(null);
  const [ladowanieWdrozen, setLadowanieWdrozen] = useState(true);
  const [filtrSrodowiska, setFiltrSrodowiska] = useState('wszystkie');
  const [rozwiniete, setRozwiniete] = useState([]);

  // Niektore wpisy (np. seedowany v3.0.0) maja changes jako liste obiektow
  // { category, items: [...] } zamiast plaskiej listy stringow. Renderowanie
  // obiektu jako dziecka React rzuca "Objects are not valid as a React child"
  // i wywala cala zakladke. Normalizujemy do plaskiej listy stringow.
  const normalizeChanges = (changes) => {
    if (!Array.isArray(changes)) return [];
    const result = [];
    changes.forEach((change) => {
      if (typeof change === 'string') {
        result.push(change);
      } else if (change && typeof change === 'object') {
        const category = change.category || change.kategoria || '';
        const items = Array.isArray(change.items) ? change.items : [];
        if (items.length > 0) {
          items.forEach((item) => {
            const text = typeof item === 'string' ? item : String(item);
            result.push(category ? `${category}: ${text}` : text);
          });
        } else if (category) {
          result.push(category);
        }
      } else if (change != null) {
        result.push(String(change));
      }
    });
    return result;
  };

  const loadEntries = async () => {
    try {
      setLoading(true);
      // Ciche: blad pokazuje pasek 'message' ponizej, wiec toast i dwa
      // automatyczne ponowienia z interceptora (services/api.js) tylko
      // przeszkadzaja. Zapis/edycja/usuwanie zostaja glosne.
      const response = await api.get('/changelog?limit=50', { silent: true, noRetry: true });
      if (response.data.success) {
        setEntries(response.data.data.items || []);
      }
    } catch (error) {
      console.error('Błąd pobierania changelog:', error);
      setMessage({ type: 'error', text: 'Błąd pobierania wpisów' });
    } finally {
      setLoading(false);
    }
  };

  // Aktualna wersja tej instancji (numer, commit, srodowisko, ostatnie wdrozenie)
  const wczytajWersje = useCallback(async () => {
    setLadowanieWersji(true);
    const dane = await pobierzAktualnaWersje(api);
    setWersja(dane);
    setLadowanieWersji(false);
  }, []);

  // Historia wdrozen — filtr srodowiska obsluguje backend
  const wczytajWdrozenia = useCallback(async (srodowisko) => {
    setLadowanieWdrozen(true);
    const { pozycje, blad } = await pobierzHistorieWdrozen(api, {
      limit: 50,
      srodowisko: srodowisko === 'wszystkie' ? null : srodowisko
    });
    setWdrozenia(pozycje);
    setBladWdrozen(blad);
    setLadowanieWdrozen(false);
  }, []);

  useEffect(() => {
    loadEntries();
    wczytajWersje();
  }, [wczytajWersje]);

  useEffect(() => {
    wczytajWdrozenia(filtrSrodowiska);
  }, [filtrSrodowiska, wczytajWdrozenia]);

  const przelaczOpis = (id) => {
    setRozwiniete((poprzednie) => (
      poprzednie.includes(id)
        ? poprzednie.filter((x) => x !== id)
        : [...poprzednie, id]
    ));
  };

  const odswiezWszystko = () => {
    wczytajWersje();
    wczytajWdrozenia(filtrSrodowiska);
  };

  const resetForm = () => {
    setFormData({
      version: '',
      title: '',
      description: '',
      changes: [''],
      is_important: false
    });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      setMessage({ type: 'error', text: 'Tytuł jest wymagany' });
      return;
    }

    try {
      setSaving(true);

      // Filtruj puste zmiany (normalizacja na wypadek obiektow w changes)
      const changesFiltered = normalizeChanges(formData.changes).filter(c => c.trim() !== '');

      const payload = {
        version: formData.version,
        title: formData.title,
        description: formData.description,
        changes: changesFiltered,
        is_important: formData.is_important
      };

      let response;
      if (editingId) {
        response = await api.put(`/changelog/${editingId}`, payload);
      } else {
        response = await api.post('/changelog', payload);
      }

      if (response.data.success) {
        setMessage({ type: 'success', text: editingId ? 'Wpis zaktualizowany!' : 'Wpis dodany pomyślnie!' });
        resetForm();
        loadEntries();
      } else {
        setMessage({ type: 'error', text: response.data.message || 'Błąd zapisywania wpisu' });
      }
    } catch (error) {
      console.error('Błąd zapisywania:', error);
      setMessage({ type: 'error', text: 'Błąd komunikacji z serwerem' });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (entry) => {
    const normalized = normalizeChanges(entry.changes);
    setFormData({
      version: entry.version || '',
      title: entry.title || '',
      description: entry.description || '',
      changes: normalized.length > 0 ? normalized : [''],
      is_important: entry.is_important || false
    });
    setEditingId(entry.id);
    setShowForm(true);
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Czy na pewno chcesz usunąć ten wpis?')) {
      return;
    }

    try {
      const response = await api.delete(`/changelog/${entryId}`);
      if (response.data.success) {
        setMessage({ type: 'success', text: 'Wpis usunięty!' });
        loadEntries();
      } else {
        setMessage({ type: 'error', text: 'Błąd usuwania wpisu' });
      }
    } catch (error) {
      console.error('Błąd usuwania:', error);
      setMessage({ type: 'error', text: 'Błąd komunikacji z serwerem' });
    }
  };

  const addChangeField = () => {
    setFormData(prev => ({
      ...prev,
      changes: [...prev.changes, '']
    }));
  };

  const removeChangeField = (index) => {
    setFormData(prev => ({
      ...prev,
      changes: prev.changes.filter((_, i) => i !== index)
    }));
  };

  const updateChange = (index, value) => {
    setFormData(prev => ({
      ...prev,
      changes: prev.changes.map((c, i) => i === index ? value : c)
    }));
  };

  const formatDate = (dateStr) => formatujDate(dateStr);

  // --- boks aktualnej wersji ---

  const ostatnie = wersja ? wersja.ostatnieWdrozenie : null;
  const opisBiezacegoSrodowiska = opisSrodowiska(wersja ? wersja.srodowisko : null);

  // Commit z gita na serwerze (krotki) kontra commit ostatniego zapisanego
  // wdrozenia — rozjazd znaczy, ze ktos ruszal repozytorium poza deploy.sh
  const commitBiezacy = wersja ? skrocCommit(wersja.commit, 7) : null;
  const commitZapisany = ostatnie ? skrocCommit(ostatnie.commit, 7) : null;
  const rozjazdCommita = Boolean(commitBiezacy && commitZapisany && commitBiezacy !== commitZapisany);

  const renderBoksWersji = () => (
    <div style={{
      backgroundColor: 'var(--card-bg)',
      border: '1px solid var(--border)',
      borderLeft: `5px solid ${opisBiezacegoSrodowiska.tlo}`,
      borderRadius: '8px',
      padding: '1rem 1.25rem',
      marginBottom: ODSTEP_SEKCJI,
      boxShadow: 'var(--shadow)'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: '1rem',
        flexWrap: 'wrap'
      }}>
        <div style={{ minWidth: 0 }}>
          <div style={{
            fontSize: '0.75rem',
            fontWeight: '600',
            color: 'var(--text-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: '0.35rem'
          }}>
            Aktualna wersja systemu
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            <span style={{
              fontSize: '1.75rem',
              fontWeight: '700',
              color: 'var(--text)',
              lineHeight: 1.1
            }}>
              {ladowanieWersji ? '…' : (wersja && wersja.wersja ? `v${wersja.wersja}` : 'nieznana')}
            </span>
            <PlakietkaSrodowiska srodowisko={wersja ? wersja.srodowisko : null} duza />
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            flexWrap: 'wrap',
            marginTop: '0.6rem',
            fontSize: '0.8rem',
            color: 'var(--text-muted)'
          }}>
            {commitBiezacy && (
              <span title="Commit, który działa na tym serwerze">
                commit <span style={stylCommita}>{commitBiezacy}</span>
              </span>
            )}
            {ostatnie && ostatnie.data ? (
              <span>wdrożono: <strong style={{ color: 'var(--text-secondary)' }}>{formatujDate(ostatnie.data)}</strong></span>
            ) : (
              wersja && wersja.commitData && (
                <span>commit z dnia: <strong style={{ color: 'var(--text-secondary)' }}>{formatujDate(wersja.commitData)}</strong></span>
              )
            )}
            {ostatnie && ostatnie.wdrozyl && (
              <span>wdrożył: <strong style={{ color: 'var(--text-secondary)' }}>{ostatnie.wdrozyl}</strong></span>
            )}
          </div>

          <div style={{
            marginTop: '0.5rem',
            fontSize: '0.75rem',
            color: 'var(--text-muted)'
          }}>
            {opisBiezacegoSrodowiska.opis}
          </div>
        </div>

        <button
          onClick={odswiezWszystko}
          title="Pobierz ponownie informacje o wersji i historię wdrożeń"
          style={{
            padding: '6px 12px',
            backgroundColor: 'var(--info)',
            color: '#fff',
            border: '1px solid var(--info)',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: '500',
            flexShrink: 0
          }}
        >
          🔄 Odśwież
        </button>
      </div>

      {rozjazdCommita && (
        <div style={{
          marginTop: '0.75rem',
          padding: '0.5rem 0.75rem',
          backgroundColor: 'var(--warning-light)',
          border: '1px solid var(--warning)',
          borderRadius: '4px',
          fontSize: '0.78rem',
          color: 'var(--text)'
        }}>
          Uwaga: na serwerze działa commit <strong>{commitBiezacy}</strong>, a ostatnie zapisane
          wdrożenie to <strong>{commitZapisany}</strong>. Kod mógł zostać zmieniony poza deploy.sh.
        </div>
      )}

      {wersja && wersja.blad && (
        <div style={{
          marginTop: '0.75rem',
          fontSize: '0.78rem',
          color: 'var(--danger)'
        }}>
          {wersja.blad}
        </div>
      )}
    </div>
  );

  // --- historia wdrozen ---

  const filtry = [
    { id: 'wszystkie', etykieta: 'Wszystkie' },
    { id: 'test', etykieta: 'Test' },
    { id: 'produkcja', etykieta: 'Produkcja' }
  ];

  const renderHistorieWdrozen = () => (
    <div style={{ marginBottom: ODSTEP_SEKCJI }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '0.5rem',
        flexWrap: 'wrap',
        marginBottom: '0.75rem'
      }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '1.05rem' }}>Historia wdrożeń</h3>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>
            Wpisy powstają automatycznie po każdym udanym wdrożeniu
          </div>
        </div>

        {/* Filtr srodowiska — styl zakladek: 11px, padding 4px 10px */}
        <div style={{ display: 'flex', gap: '0.25rem' }}>
          {filtry.map((filtr) => {
            const aktywny = filtrSrodowiska === filtr.id;
            return (
              <button
                key={filtr.id}
                onClick={() => setFiltrSrodowiska(filtr.id)}
                style={{
                  padding: '4px 10px',
                  fontSize: '11px',
                  fontWeight: '500',
                  // tlo LINIOWE — inaczej motyw nadpisze je regula z !important
                  backgroundColor: aktywny ? 'var(--info)' : 'var(--card-bg)',
                  color: aktywny ? '#fff' : 'var(--text-secondary)',
                  border: `1px solid ${aktywny ? 'var(--info)' : 'var(--border)'}`,
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                {filtr.etykieta}
              </button>
            );
          })}
        </div>
      </div>

      {ladowanieWdrozen ? (
        <div style={{ textAlign: 'center', padding: '1.5rem', color: 'var(--text-muted)' }}>
          Ładowanie historii wdrożeń...
        </div>
      ) : bladWdrozen ? (
        <div style={{
          padding: '1rem',
          backgroundColor: 'var(--card-bg)',
          border: '1px solid var(--border)',
          borderRadius: '8px',
          color: 'var(--text-muted)',
          fontSize: '0.85rem'
        }}>
          {bladWdrozen}
        </div>
      ) : wdrozenia.length === 0 ? (
        <div style={{
          padding: '1.25rem',
          backgroundColor: 'var(--card-bg)',
          border: '1px dashed var(--border)',
          borderRadius: '8px',
          color: 'var(--text-muted)',
          fontSize: '0.85rem',
          textAlign: 'center'
        }}>
          Brak zapisanych wdrożeń{filtrSrodowiska !== 'wszystkie' ? ' dla tego środowiska' : ''}.
          Pierwszy wpis pojawi się po najbliższym wdrożeniu.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {wdrozenia.map((wdrozenie) => {
            const opisSrod = opisSrodowiska(wdrozenie.srodowisko);
            const czyRozwiniete = rozwiniete.includes(wdrozenie.id);
            const maOpis = Boolean(wdrozenie.opis && wdrozenie.opis.trim());

            return (
              <div
                key={wdrozenie.id}
                style={{
                  backgroundColor: 'var(--card-bg)',
                  border: '1px solid var(--border)',
                  borderLeft: `4px solid ${opisSrod.tlo}`,
                  borderRadius: '6px',
                  padding: '0.75rem 1rem'
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: '0.75rem',
                  flexWrap: 'wrap'
                }}>
                  <div style={{ minWidth: 0, flex: '1 1 260px' }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.4rem',
                      flexWrap: 'wrap',
                      marginBottom: '0.4rem'
                    }}>
                      {wdrozenie.wersja && (
                        // ciemny tekst na zieleni — w trybie ciemnym --success
                        // jest jasny i biale litery bylyby nieczytelne
                        <span style={stylPlakietki('var(--success)', '#212529')}>
                          v{wdrozenie.wersja}
                        </span>
                      )}
                      <PlakietkaSrodowiska srodowisko={wdrozenie.srodowisko} />
                      <PlakietkaZrodla automatyczny={wdrozenie.automatyczny} />
                    </div>

                    <div style={{
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      color: 'var(--text)',
                      marginBottom: '0.3rem',
                      wordBreak: 'break-word'
                    }}>
                      {wdrozenie.tytul}
                    </div>

                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.6rem',
                      flexWrap: 'wrap',
                      fontSize: '0.75rem',
                      color: 'var(--text-muted)'
                    }}>
                      {wdrozenie.commitKrotki && (
                        <span style={stylCommita}>{wdrozenie.commitKrotki}</span>
                      )}
                      {wdrozenie.wdrozyl && <span>wdrożył: {wdrozenie.wdrozyl}</span>}
                    </div>
                  </div>

                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '0.4rem' }}>
                      {formatujDate(wdrozenie.data)}
                    </div>
                    {maOpis && (
                      <button
                        onClick={() => przelaczOpis(wdrozenie.id)}
                        style={{
                          padding: '4px 10px',
                          fontSize: '11px',
                          fontWeight: '500',
                          backgroundColor: 'var(--card-bg)',
                          color: 'var(--text-secondary)',
                          border: '1px solid var(--border)',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        {czyRozwiniete ? 'Ukryj opis ▲' : 'Pokaż opis ▼'}
                      </button>
                    )}
                  </div>
                </div>

                {maOpis && czyRozwiniete && (
                  <pre style={{
                    margin: '0.75rem 0 0 0',
                    padding: '0.75rem',
                    backgroundColor: 'var(--bg-secondary)',
                    border: '1px solid var(--border)',
                    borderRadius: '4px',
                    fontSize: '0.78rem',
                    color: 'var(--text-secondary)',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily: 'inherit',
                    maxHeight: '320px',
                    overflowY: 'auto'
                  }}>
                    {wdrozenie.opis}
                  </pre>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div style={{ padding: '1rem' }}>
      {/* 1. Co dokladnie dziala na tym serwerze */}
      {renderBoksWersji()}

      {/* 2. Historia wdrozen (automat z deploy.sh) */}
      {renderHistorieWdrozen()}

      {/* 3. Changelog redagowany recznie */}
      <div style={{
        borderTop: '1px solid var(--border)',
        paddingTop: '1.25rem'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem',
          gap: '0.5rem',
          flexWrap: 'wrap'
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.05rem' }}>Changelog ręczny</h3>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>
              Wpisy redagowane przez administratora — niezależne od historii wdrożeń
            </div>
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            style={{
              padding: '6px 12px',
              backgroundColor: showForm ? 'var(--text-muted)' : 'var(--success)',
              color: 'white',
              border: 'none',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '500'
            }}
          >
            {showForm ? 'Anuluj' : '+ Dodaj wpis'}
          </button>
        </div>

        {/* Message */}
        {message && (
          <div style={{
            padding: '0.75rem 1rem',
            marginBottom: '1rem',
            borderRadius: '4px',
            backgroundColor: message.type === 'success' ? 'var(--success-light)' : 'var(--danger-light)',
            color: 'var(--text)',
            border: `1px solid ${message.type === 'success' ? 'var(--success)' : 'var(--danger)'}`
          }}>
            {message.text}
            <button
              onClick={() => setMessage(null)}
              style={{
                float: 'right',
                backgroundColor: 'transparent',
                color: 'var(--text)',
                border: 'none',
                cursor: 'pointer',
                fontSize: '16px'
              }}
            >
              ✕
            </button>
          </div>
        )}

        {/* Formularz dodawania */}
        {showForm && (
          <div style={{
            backgroundColor: 'var(--card-bg)',
            padding: '1.5rem',
            borderRadius: '8px',
            marginBottom: '1.5rem',
            border: '1px solid var(--border)'
          }}>
            <h4 style={{ margin: '0 0 1rem 0' }}>
              {editingId ? 'Edytuj wpis' : 'Nowy wpis changelog'}
            </h4>

            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '500' }}>
                    Wersja
                  </label>
                  <input
                    type="text"
                    value={formData.version}
                    onChange={(e) => setFormData(prev => ({ ...prev, version: e.target.value }))}
                    placeholder="np. 1.5.0"
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid var(--border)',
                      borderRadius: '4px',
                      backgroundColor: 'var(--card-bg)',
                      color: 'var(--text)'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '500' }}>
                    Tytuł *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="np. Nowe funkcje w POS"
                    required
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid var(--border)',
                      borderRadius: '4px',
                      backgroundColor: 'var(--card-bg)',
                      color: 'var(--text)'
                    }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '500' }}>
                  Opis
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Krótki opis aktualizacji..."
                  rows={2}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid var(--border)',
                    borderRadius: '4px',
                    backgroundColor: 'var(--card-bg)',
                    color: 'var(--text)',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Lista zmian
                </label>
                {formData.changes.map((change, index) => (
                  <div key={index} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <input
                      type="text"
                      value={change}
                      onChange={(e) => updateChange(index, e.target.value)}
                      placeholder={`Zmiana ${index + 1}...`}
                      style={{
                        flex: 1,
                        padding: '0.5rem',
                        border: '1px solid var(--border)',
                        borderRadius: '4px',
                        backgroundColor: 'var(--card-bg)',
                        color: 'var(--text)'
                      }}
                    />
                    {formData.changes.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeChangeField(index)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: 'var(--danger)',
                          color: 'white',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addChangeField}
                  style={{
                    padding: '4px 10px',
                    backgroundColor: 'var(--card-bg)',
                    color: 'var(--text-secondary)',
                    border: '1px solid var(--border)',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  + Dodaj zmianę
                </button>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.is_important}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_important: e.target.checked }))}
                  />
                  <span style={{ fontWeight: '500' }}>Oznacz jako WAŻNE</span>
                  <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                    (wyróżnione czerwonym kolorem)
                  </span>
                </label>
              </div>

              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  type="submit"
                  disabled={saving}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: saving ? 'var(--text-muted)' : 'var(--info)',
                    color: 'white',
                    border: 'none',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}
                >
                  {saving ? 'Zapisywanie...' : 'Zapisz wpis'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: 'var(--card-bg)',
                    color: 'var(--text-secondary)',
                    border: '1px solid var(--border)',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  Anuluj
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Lista wpisów */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
            Ładowanie...
          </div>
        ) : entries.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
            Brak wpisów w changelogu
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {entries.map((entry) => (
              <div
                key={entry.id}
                style={{
                  backgroundColor: 'var(--card-bg)',
                  border: `2px solid ${entry.is_important ? 'var(--danger)' : 'var(--border)'}`,
                  borderRadius: '8px',
                  padding: '1rem',
                  borderLeft: `4px solid ${entry.is_important ? 'var(--danger)' : 'var(--purple)'}`
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.5rem' }}>
                  <div style={{ minWidth: 0, flex: '1 1 200px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                      {entry.version && (
                        <span style={{
                          backgroundColor: entry.is_important ? 'var(--danger)' : 'var(--info)',
                          color: 'white',
                          padding: '0.2rem 0.6rem',
                          borderRadius: '12px',
                          fontSize: '0.8rem',
                          fontWeight: '600'
                        }}>
                          v{entry.version}
                        </span>
                      )}
                      {/* Odroznienie od wpisow automatycznych z historii wdrozen */}
                      <PlakietkaZrodla automatyczny={false} />
                      {entry.is_important && (
                        <span style={stylPlakietki('var(--danger)', '#fff')}>
                          WAŻNE
                        </span>
                      )}
                    </div>
                    <h4 style={{ margin: '0 0 0.25rem 0', fontSize: '1rem' }}>
                      {entry.title}
                    </h4>
                    {entry.description && (
                      <p style={{ margin: '0 0 0.5rem 0', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                        {entry.description}
                      </p>
                    )}
                    {normalizeChanges(entry.changes).length > 0 && (
                      <ul style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.25rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                        {normalizeChanges(entry.changes).map((change, i) => (
                          <li key={i}>{change}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
                      {formatDate(entry.created_at)}
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => handleEdit(entry)}
                        style={{
                          padding: '4px 10px',
                          backgroundColor: 'var(--warning)',
                          color: '#212529',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '11px',
                          fontWeight: '500'
                        }}
                      >
                        Edytuj
                      </button>
                      <button
                        onClick={() => handleDelete(entry.id)}
                        style={{
                          padding: '4px 10px',
                          backgroundColor: 'var(--danger)',
                          color: 'white',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '11px',
                          fontWeight: '500'
                        }}
                      >
                        Usuń
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChangelogAdmin;
